import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useGRN } from '@/hooks/finance/useGRN';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface GRNLineItem {
  id: string;
  product_name: string;
  unit: string;
  ordered_quantity: number;
  received_quantity: number;
  quality_notes?: string;
  isNew?: boolean;
}

const EditGRNPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: grn, isLoading } = useGRN(id || '');

  const [formData, setFormData] = useState({
    delivery_date: '',
    delivery_note_number: '',
    vehicle_number: '',
    driver_name: '',
    warehouse_location: '',
    notes: '',
  });

  const [items, setItems] = useState<GRNLineItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (grn) {
      setFormData({
        delivery_date: grn.delivery_date,
        delivery_note_number: grn.delivery_note_number || '',
        vehicle_number: grn.vehicle_number || '',
        driver_name: grn.driver_name || '',
        warehouse_location: grn.warehouse_location || '',
        notes: grn.notes || '',
      });
      
      if (grn.items && grn.items.length > 0) {
        setItems(grn.items.map(item => ({
          id: item.id,
          product_name: item.product_name,
          unit: item.unit || 'kg',
          ordered_quantity: item.ordered_quantity,
          received_quantity: item.received_quantity,
          quality_notes: item.quality_notes || '',
        })));
      } else {
        setItems([{ 
          id: '1', 
          product_name: '', 
          unit: 'kg', 
          ordered_quantity: 0, 
          received_quantity: 0,
          isNew: true,
        }]);
      }
    }
  }, [grn]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (itemId: string, field: keyof GRNLineItem, value: string | number) => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  const addItem = () => {
    setItems(prev => [...prev, { 
      id: `new-${Date.now()}`, 
      product_name: '', 
      unit: 'kg', 
      ordered_quantity: 0, 
      received_quantity: 0,
      isNew: true,
    }]);
  };

  const removeItem = (itemId: string) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== itemId));
    }
  };

  const handleSubmit = async () => {
    if (!id || !formData.delivery_date) {
      toast.error('Please enter delivery date');
      return;
    }

    if (items.every(i => !i.product_name)) {
      toast.error('Please add at least one item');
      return;
    }

    setIsSubmitting(true);
    try {
      // Update GRN header
      const { error: grnError } = await (supabase as any)
        .from('grn')
        .update(formData)
        .eq('id', id);
      
      if (grnError) throw grnError;

      // Handle items - update existing, add new, delete removed
      const existingItems = items.filter(i => !i.isNew && i.product_name);
      const newItems = items.filter(i => i.isNew && i.product_name);
      const deletedItemIds = grn?.items
        ?.filter(originalItem => !items.find(i => i.id === originalItem.id))
        .map(i => i.id) || [];

      // Delete removed items
      if (deletedItemIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('grn_items')
          .delete()
          .in('id', deletedItemIds);
        
        if (deleteError) throw deleteError;
      }

      // Update existing items
      for (const item of existingItems) {
        const { error: updateError } = await supabase
          .from('grn_items')
          .update({
            product_name: item.product_name,
            unit: item.unit,
            ordered_quantity: item.ordered_quantity,
            received_quantity: item.received_quantity,
            quality_notes: item.quality_notes,
          })
          .eq('id', item.id);
        
        if (updateError) throw updateError;
      }

      // Insert new items
      if (newItems.length > 0) {
        const { error: insertError } = await supabase
          .from('grn_items')
          .insert(newItems.map(item => ({
            grn_id: id,
            product_name: item.product_name,
            unit: item.unit,
            ordered_quantity: item.ordered_quantity,
            received_quantity: item.received_quantity,
            quality_notes: item.quality_notes,
          })));
        
        if (insertError) throw insertError;
      }

      queryClient.invalidateQueries({ queryKey: ['grns'] });
      queryClient.invalidateQueries({ queryKey: ['grn', id] });
      toast.success('GRN updated successfully');
      navigate(`/finance/accounting/grn/${id}`);
    } catch (error) {
      console.error('Failed to update GRN:', error);
      toast.error('Failed to update GRN');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6 max-w-4xl mx-auto">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </Layout>
    );
  }

  if (!grn) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">GRN not found</h2>
          <Button className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Edit GRN</h1>
            <p className="text-muted-foreground">{grn.grn_number}</p>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Delivery Information */}
          <Card>
            <CardHeader>
              <CardTitle>Delivery Information</CardTitle>
              <CardDescription>Details about the delivery</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="delivery_date">Delivery Date *</Label>
                  <Input
                    id="delivery_date"
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) => handleInputChange('delivery_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delivery_note_number">Delivery Note Number</Label>
                  <Input
                    id="delivery_note_number"
                    placeholder="DN-12345"
                    value={formData.delivery_note_number}
                    onChange={(e) => handleInputChange('delivery_note_number', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="vehicle_number">Vehicle Number</Label>
                  <Input
                    id="vehicle_number"
                    placeholder="Plate number"
                    value={formData.vehicle_number}
                    onChange={(e) => handleInputChange('vehicle_number', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driver_name">Driver Name</Label>
                  <Input
                    id="driver_name"
                    placeholder="Driver's name"
                    value={formData.driver_name}
                    onChange={(e) => handleInputChange('driver_name', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="warehouse_location">Warehouse Location</Label>
                <Input
                  id="warehouse_location"
                  placeholder="Where goods are being stored"
                  value={formData.warehouse_location}
                  onChange={(e) => handleInputChange('warehouse_location', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card>
            <CardHeader>
              <CardTitle>Items Received</CardTitle>
              <CardDescription>List of goods received</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-3 items-start p-4 border rounded-lg">
                    <div className="flex-1 space-y-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Product Name *</Label>
                          <Input
                            placeholder="Product name"
                            value={item.product_name}
                            onChange={(e) => handleItemChange(item.id, 'product_name', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Unit</Label>
                          <Select
                            value={item.unit}
                            onValueChange={(v) => handleItemChange(item.id, 'unit', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="kg">Kilograms (kg)</SelectItem>
                              <SelectItem value="units">Units</SelectItem>
                              <SelectItem value="boxes">Boxes</SelectItem>
                              <SelectItem value="crates">Crates</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Ordered Quantity</Label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={item.ordered_quantity || ''}
                            onChange={(e) => handleItemChange(item.id, 'ordered_quantity', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Received Quantity *</Label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={item.received_quantity || ''}
                            onChange={(e) => handleItemChange(item.id, 'received_quantity', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Quality Notes</Label>
                        <Input
                          placeholder="Any quality observations"
                          value={item.quality_notes || ''}
                          onChange={(e) => handleItemChange(item.id, 'quality_notes', e.target.value)}
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(item.id)}
                      disabled={items.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button variant="outline" onClick={addItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Any additional notes about this delivery..."
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate(-1)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EditGRNPage;
